// Event category system from the "Atlanta Editorial" design refresh.
// Each category has a hue; all chip colors are derived from that hue plus the
// theme's chip tokens (see themes.js) so the palette stays coherent across
// light and dark. Source: design_handoff_calendar_views/README.md.

export const CATEGORIES = {
    conference: { label: 'Conference & Summit', hue: 250, icon: 'fa-microphone-alt' },
    networking: { label: 'Networking & Social', hue: 48,  icon: 'fa-users' },
    workshop:   { label: 'Workshop & Meetup',   hue: 158, icon: 'fa-laptop-code' },
    security:   { label: 'Security & Cyber',    hue: 330, icon: 'fa-shield-alt' },
    community:  { label: 'Community & Culture', hue: 288, icon: 'fa-theater-masks' },
    holiday:    { label: 'Holiday',             hue: 0,   icon: 'fa-gift' },
};

// Categories shown in the legend footer (holiday is implicit).
export const LEGEND_ORDER = ['conference', 'networking', 'workshop', 'security', 'community'];

/**
 * Derives a category's colors from its hue + the theme's mode/chip tokens.
 * @param {string} cat - Category key from CATEGORIES
 * @param {Object} tokens - theme.tokens (mode, colors with chip tokens)
 * @returns {{strong: string, line: string, fillBg: string, fillText: string}}
 */
export function catColor(cat, tokens) {
    const meta = CATEGORIES[cat] || CATEGORIES.community;
    const { hue } = meta;
    const isHoliday = cat === 'holiday';
    const c = tokens.colors;
    const dark = tokens.mode === 'dark';
    const strongL = dark ? 0.74 : 0.46;
    const strongC = isHoliday ? 0.01 : 0.15;
    const alpha = dark ? ' / 0.55' : '';
    return {
        strong: `oklch(${strongL} ${strongC} ${hue})`,
        line: `oklch(${strongL} ${strongC} ${hue} / 0.32)`,
        fillBg: `oklch(${c.chipL} ${isHoliday ? 0.005 : c.chipC} ${hue}${alpha})`,
        fillText: `oklch(${c.chipTextL} ${isHoliday ? 0.01 : c.chipTextC} ${hue})`,
    };
}

const matchers = [
    ['holiday', /holiday/i],
    ['security', /security|cyber|hack|issa|infosec|defen[cs]e|privacy/i],
    ['conference', /conference|summit|expo|symposium|con\b/i],
    ['networking', /network|social|mixer|happy hour|chowdown|connect|golf|party/i],
    ['workshop', /workshop|meetup|hands-on|bootcamp|study|code|builders|pitch practice|demo/i],
];

// Also used when several pre-categorized listings are merged into one
// co-hosted event. Keeping this derived from the matcher order ensures a
// merged event is categorized with the same precedence as a single event.
export const CATEGORY_MATCH_ORDER = [...matchers.map(([category]) => category), 'community'];

/**
 * Maps a real event onto one of the six design categories by inspecting its
 * tags, title, and organization. Falls back to 'community'.
 * @param {Object} event - Event with title, organization, and optional tags
 * @returns {string} Category key from CATEGORIES
 */
export function categorizeEvent(event) {
    if (event?.derivedCategory && CATEGORIES[event.derivedCategory]) {
        return event.derivedCategory;
    }
    const tagText = Array.isArray(event.tags)
        ? event.tags.map((t) => (typeof t === 'string' ? t : t?.name || '')).join(' ')
        : (typeof event.tags === 'string' ? event.tags : '');
    const haystack = `${tagText} ${event.title || ''} ${event.organization || ''}`;
    for (const [cat, re] of matchers) {
        if (re.test(haystack)) return cat;
    }
    return 'community';
}
